import {
  Button,
  Container,
  Group,
  Paper,
  Stack,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCreateProject, type CreateProjectData } from "@/hooks/useProjects";

export const Route = createFileRoute("/projects/new")({
  component: NewProjectPage,
});

function NewProjectPage() {
  const navigate = useNavigate();
  const createProject = useCreateProject();

  const form = useForm<CreateProjectData>({
    initialValues: {
      name: "",
      address: "",
      city: "",
      state: "",
      zip: "",
    },
    validate: {
      name: (value) =>
        value.trim().length < 2 ? "Project name is required" : null,
    },
  });

  const handleSubmit = form.onSubmit(async (values) => {
    try {
      const result = await createProject.mutateAsync(values);
      notifications.show({
        title: "Success",
        message: "Project created successfully!",
        color: "green",
      });
      navigate({ to: "/projects/$projectId", params: { projectId: result.project.id } });
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to create project",
        color: "red",
      });
    }
  });

  const handleCancel = () => {
    navigate({ to: "/projects" });
  };

  return (
    <Container size="sm" py="xl">
      <Paper p="xl" withBorder>
        <Stack gap="md">
          <Title order={2}>Create New Project</Title>

          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              <TextInput
                label="Project Name"
                placeholder="Enter project name"
                {...form.getInputProps("name")}
                required
              />

              <TextInput
                label="Address"
                placeholder="Enter street address"
                {...form.getInputProps("address")}
              />

              <Group grow>
                <TextInput
                  label="City"
                  placeholder="City"
                  {...form.getInputProps("city")}
                />
                <TextInput
                  label="State"
                  placeholder="State"
                  {...form.getInputProps("state")}
                />
              </Group>

              <TextInput
                label="ZIP Code"
                placeholder="ZIP code"
                {...form.getInputProps("zip")}
              />

              <Group justify="flex-end" mt="md">
                <Button
                  variant="subtle"
                  onClick={handleCancel}
                  disabled={createProject.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={createProject.isPending}
                >
                  Create Project
                </Button>
              </Group>
            </Stack>
          </form>
        </Stack>
      </Paper>
    </Container>
  );
}
